import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { InsInsightOutcomeResponse } from '@plato/data/prisma';
import { SrInsightType as InsightType } from '@plato/data/starrocks/types';
import { PercentageOrEmpty } from '@plato/ui-legacy/components/typography/percentage-or-empty';
import { roundAbsToHundred } from '@plato/ui-legacy/lib/round-abs-to-hundred';
import {
    Card,
    CardActions,
    CardActionsGroup,
    CardContent,
    CardDetail,
    CardExpandAction,
    CardExpandContent,
    CardFront,
    CardHeader,
    CardStart,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import { Popover, PopoverContent, PopoverTrigger } from '@plato/ui/containers/popover';
import { Menu, MenuItem, MenuItemLabel, MenuItemTrailing } from '@plato/ui/primitives/menu';
import { Skeleton } from '@plato/ui/primitives/skeleton';
import { cn } from '@plato/ui/util/classes';
import type { Namespace, TFunction } from 'i18next';
import { CheckIcon, ChevronDownIcon, TrendingDownIcon, TrendingUpIcon, XIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { UpsertInsInsightOutcomeDTO } from '../../../server/dto/ins-insight-outcome-dto.ts';
import type { InsightListItem } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { InsightCardFront } from '../../insights-common/components-new/insight-card-front.tsx';
import { InsightReasonText } from '../../insights-common/components/insight-reason-text.tsx';
import { MoneyDeltaBadge } from '../../insights-common/components/money-delta-badge.tsx';
import { isRiskInsightType } from '../../insights-common/lib/insight-bucket.ts';
import { getInsightEntityName } from '../../insights-common/lib/insight-entity-name.ts';
import { getInsightName } from '../../insights-common/lib/insight-name.ts';

export type LogInteractionInsightCardProps = {
    insightData: InsightListItem & {
        outcome?: UpsertInsInsightOutcomeDTO;
    };
    onExpand?: () => void;
    onSelectResponse: (response: InsInsightOutcomeResponse) => void;
    onRemoveOutcome: () => void;
};

const maxVisibleArticles = 3;
// Mirrors insight-product-recommendations-query-detail.ts's per-group cap. This card has no
// brand-grouping UI, so behind the flag it normalizes back to that plain top-N/rank contract.
const maxRecommendationItems = 5;

const outcomeResponseOptions = [
    InsInsightOutcomeResponse.INTERESTED,
    InsInsightOutcomeResponse.NO_NEED,
    InsInsightOutcomeResponse.BUYS_ELSEWHERE,
] as const;

function outcomeResponseLabel(
    t: TFunction<Namespace>,
    response: InsInsightOutcomeResponse,
    withPrefix = false,
): string {
    const prefix = withPrefix ? `${t('insight_outcome__response_prefix', { ns: 'insights' })} · ` : '';

    switch (response) {
        case InsInsightOutcomeResponse.INTERESTED:
            return t('insight_outcome__response_INTERESTED', { ns: 'insights' });
        case InsInsightOutcomeResponse.NO_NEED:
            return `${prefix}${t('insight_outcome__response_NO_NEED', { ns: 'insights' })}`;
        case InsInsightOutcomeResponse.BUYS_ELSEWHERE:
            return `${prefix}${t('insight_outcome__response_BUYS_ELSEWHERE', { ns: 'insights' })}`;
        default:
            return '';
    }
}

export function LogInteractionInsightCard({
    insightData,
    onExpand,
    onSelectResponse,
    onRemoveOutcome,
}: LogInteractionInsightCardProps): ReactNode {
    const { t } = useTranslation('notes');

    const [isOpen, setIsOpen] = useState(false);
    const [isOutcomeMenuOpen, setIsOutcomeMenuOpen] = useState(false);

    const { insight } = insightData;
    const name = getInsightName(t, insight);
    const category = getInsightEntityName(t, insightData);
    const DirectionIcon = isRiskInsightType(insight.insight_type) ? TrendingDownIcon : TrendingUpIcon;

    const response = insightData.outcome?.response;
    const hasOutcome = insightData.outcome !== undefined;

    const handleSelectResponse = (selected: InsInsightOutcomeResponse): void => {
        onSelectResponse(selected);
        setIsOutcomeMenuOpen(false);
        setIsOpen(false);
    };

    const handleClearOutcome = (): void => {
        onRemoveOutcome();
        setIsOutcomeMenuOpen(false);
    };

    return (
        <Card
            open={isOpen}
            onOpenChange={(open) => {
                setIsOpen(open);
                if (open) {
                    onExpand?.();
                }
            }}
            className={cn(hasOutcome && 'l2:border-dashed')}
        >
            {hasOutcome && response !== undefined && !isOpen ? (
                <ResolvedInsightFront
                    icon={<DirectionIcon />}
                    name={name}
                    amount={roundAbsToHundred(insight.absolute_number ?? 0)}
                    category={category}
                    response={response}
                />
            ) : (
                <InsightCardFront insightData={insightData}>
                    <CardExpandAction className="self-start" />
                </InsightCardFront>
            )}
            <CardExpandContent>
                <div className="flex min-w-0 flex-1 flex-col gap-container">
                    <span className="text-body text-content-weaker">
                        <InsightReasonText insight={insight} categoryName={category} />
                    </span>
                    {isOpen && <InsightArticles insightData={insightData} />}
                </div>
            </CardExpandContent>
            <CardActions>
                <CardActionsGroup className="grow justify-end">
                    <Popover open={isOutcomeMenuOpen} onOpenChange={setIsOutcomeMenuOpen}>
                        <PopoverTrigger variant={hasOutcome ? 'primary' : 'secondary'}>
                            {response === undefined
                                ? t('notes__ai_insights_outcome_action')
                                : outcomeResponseLabel(t, response)}
                            <ChevronDownIcon />
                        </PopoverTrigger>
                        <PopoverContent align="end" className="min-w-48">
                            <Menu>
                                {outcomeResponseOptions.map((option) => (
                                    <MenuItem
                                        key={option}
                                        label={outcomeResponseLabel(t, option)}
                                        onClick={() => {
                                            handleSelectResponse(option);
                                        }}
                                    >
                                        <MenuItemLabel />
                                        {response === option && (
                                            <MenuItemTrailing>
                                                <CheckIcon className="stroke-[2.5] text-brand" />
                                            </MenuItemTrailing>
                                        )}
                                    </MenuItem>
                                ))}
                                <MenuItem
                                    label={t('notes__ai_insights_outcome_none')}
                                    onClick={handleClearOutcome}
                                >
                                    <MenuItemLabel />
                                    {!hasOutcome && (
                                        <MenuItemTrailing>
                                            <CheckIcon className="stroke-[2.5] text-brand" />
                                        </MenuItemTrailing>
                                    )}
                                </MenuItem>
                            </Menu>
                        </PopoverContent>
                    </Popover>
                </CardActionsGroup>
            </CardActions>
        </Card>
    );
}

type InsightFrontProps = {
    icon: ReactNode;
    name: string;
    amount: number;
    category: string;
};

function ResolvedInsightFront({
    icon,
    name,
    amount,
    category,
    response,
}: InsightFrontProps & { response: InsInsightOutcomeResponse }): ReactNode {
    const { t } = useTranslation('notes');
    const isInterested = response === InsInsightOutcomeResponse.INTERESTED;

    return (
        <CardFront>
            <CardStart>{icon}</CardStart>
            <CardContent>
                <CardHeader
                    className={cn('flex-nowrap items-center', {
                        'line-through': !isInterested,
                    })}
                >
                    <CardTitle className="min-w-0 flex-1 truncate font-semibold">{name}</CardTitle>
                    <CardSubtitle className="shrink-0 items-center tabular-nums">
                        <MoneyDeltaBadge className="text-content-weaker">{amount}</MoneyDeltaBadge>
                    </CardSubtitle>
                </CardHeader>
                <CardDetail className="flex items-center gap-cozy justify-between">
                    <span className={cn('min-w-0 truncate', { 'line-through': !isInterested })}>
                        {category}
                    </span>
                    <span className="flex shrink-0 items-center gap-tight text-caption text-content-weaker">
                        {isInterested ? <CheckIcon className="size-3.5" /> : <XIcon className="size-3.5" />}
                        {outcomeResponseLabel(t, response, true)}
                    </span>
                </CardDetail>
            </CardContent>
            <CardExpandAction className="self-start" />
        </CardFront>
    );
}

function InsightArticles({ insightData }: { insightData: InsightListItem }): ReactNode {
    if (insightData.insight.insight_type === InsightType.CHURN_WARNING) {
        return <ChurnArticles insightData={insightData} />;
    }
    return <RecommendationArticles insightData={insightData} />;
}

function ChurnArticles({ insightData }: { insightData: InsightListItem }): ReactNode {
    const { data, isLoading } =
        api.platoUser.query.insights.getInsightExtendedView.getChurnWarningDetail.useQuery({
            insightId: insightData.insight.id,
        });

    if (isLoading) {
        return <ArticlesLoading />;
    }

    const products = data ?? [];
    const shown = products.slice(0, maxVisibleArticles);
    const rest = products.length - shown.length;

    if (shown.length === 0) {
        return null;
    }

    return (
        <ArticleList rest={rest}>
            {shown.map((product) => {
                const delta = product.revenue_delta_percentage;
                const up = delta === null || delta === 0 ? null : delta > 0;
                return (
                    <ArticleRow key={product.id} name={product.name}>
                        <span className="shrink-0 text-body tabular-nums text-content-weaker">
                            <CurrencyOrEmpty showEmptyIfZero>{product.revenue_current}</CurrencyOrEmpty>
                        </span>
                        <span
                            className={cn(
                                'w-16 shrink-0 text-right text-body tabular-nums',
                                up === null ? 'text-content-weaker' : up ? 'text-success' : 'text-danger',
                            )}
                        >
                            <PercentageOrEmpty showPlusSignIfPositive showEmptyIfZero>
                                {delta}
                            </PercentageOrEmpty>
                        </span>
                    </ArticleRow>
                );
            })}
        </ArticleList>
    );
}

function RecommendationArticles({ insightData }: { insightData: InsightListItem }): ReactNode {
    const { data, isLoading } =
        api.platoUser.query.insights.getInsightExtendedView.getProductRecommendationsDetail.useQuery({
            insightId: insightData.insight.id,
        });

    if (isLoading) {
        return <ArticlesLoading />;
    }

    // The shared query can return up to 10 items ordered preferred-brand
    // first instead of 5 in rank order. This compact preview has no brand-grouping UI, so
    // restore that plain contract here rather than surfacing a reordered list unexplained.
    const products = (data ?? []).toSorted((a, b) => a.rank - b.rank).slice(0, maxRecommendationItems);
    const shown = products.slice(0, maxVisibleArticles);
    const rest = products.length - shown.length;

    if (shown.length === 0) {
        return null;
    }

    return (
        <ArticleList rest={rest}>
            {shown.map((product) => (
                <ArticleRow key={product.product_variant_id} name={product.product_variant_name} />
            ))}
        </ArticleList>
    );
}

function ArticleList({ rest, children }: { rest: number; children: ReactNode }): ReactNode {
    const { t } = useTranslation('notes');
    return (
        <div className="flex flex-col divide-y divide-line-weak">
            {children}
            {rest > 0 && (
                <span className="py-cozy text-body text-content-weaker">
                    {t('notes__ai_insights_more_articles', { count: rest })}
                </span>
            )}
        </div>
    );
}

function ArticleRow({ name, children }: { name: string; children?: ReactNode }): ReactNode {
    return (
        <div className="flex items-center gap-cozy py-cozy">
            <span className="min-w-0 flex-1 truncate text-body text-content">{name}</span>
            {children}
        </div>
    );
}

function ArticlesLoading(): ReactNode {
    return (
        <div className="flex flex-col gap-container-cozy py-container-tight">
            <Skeleton className="w-full" />
            <Skeleton className="w-4/5" />
            <Skeleton className="w-3/5" />
        </div>
    );
}
